import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// Функция для безопасного получения данных из localStorage
const getLocalStorageData = (key, defaultValue) => {
  const item = localStorage.getItem(key);
  try {
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error(`Ошибка при разборе данных из localStorage для ключа "${key}":`, error);
    return defaultValue;
  }
};

// Получаем актуальные роли и привилегии пользователя.
// Запрос идёт через patched fetch (см. config/axiosSetup.js): если access-токен
// протух, автоматически происходит ротация refresh-токена и повтор запроса.
export const fetchUserRoles = createAsyncThunk('auth/fetchUserRoles', async (_, { rejectWithValue, dispatch }) => {
  try {
    const response = await fetch('/api/users/profile', {
      method: 'GET',
      credentials: 'include',
    });
    if (response.status === 401 || response.status === 404) {
      // Нет валидной сессии (ротация refresh-токена не удалась или пользователь не найден)
      dispatch({ type: 'auth/logout' });
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return rejectWithValue('Сессия истекла');
    }
    if (!response.ok) throw new Error('Не удалось получить данные');
    const user = await response.json();
    const roles = (user.roles || []).map(role => role.name);
    const permissions = (user.roles || [])
        .flatMap(role => (role.privileges || []).map(privilege => privilege.name));
    return { roles, permissions };
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isAuthenticated: getLocalStorageData('isAuthenticated', false),
    email: getLocalStorageData('email', null),
    user: getLocalStorageData('user', null),
    roles: getLocalStorageData('roles', []),
    permissions: getLocalStorageData('permissions', []),
    error: null,
  },
  reducers: {
    setUser: (state, action) => {
      state.isAuthenticated = true;
      state.email = action.payload.email || state.email;
      state.user = action.payload.user || state.user;
      state.roles = action.payload.roles || state.roles;
      state.permissions = action.payload.permissions || state.permissions;
      state.error = null;
      localStorage.setItem('isAuthenticated', JSON.stringify(true));
      localStorage.setItem('email', JSON.stringify(state.email));
      localStorage.setItem('user', JSON.stringify(state.user));
      localStorage.setItem('roles', JSON.stringify(state.roles));
      localStorage.setItem('permissions', JSON.stringify(state.permissions));
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.email = null;
      state.user = null;
      state.roles = [];
      state.permissions = [];
      state.error = null;
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('email');
      localStorage.removeItem('user');
      localStorage.removeItem('roles');
      localStorage.removeItem('permissions');
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchUserRoles.fulfilled, (state, action) => {
      state.roles = action.payload.roles;
      state.permissions = action.payload.permissions;
      localStorage.setItem('roles', JSON.stringify(state.roles));
      localStorage.setItem('permissions', JSON.stringify(state.permissions));
    })
    .addCase(fetchUserRoles.rejected, (state, action) => {
      state.error = action.payload;
    });
  },
});

export const { setUser, logout, setError } = authSlice.actions;
export default authSlice.reducer;